/**
 * Cash savings sufficiency, savings-only route — the first rule of the
 * build (§15), both stages. Income is nil on this route, so the shortfall
 * equals the whole threshold (register §4). The multiplier and floor arrive
 * as rule params naming ruleset figures, so the EC/LTR rule (2.5×) and the
 * ILR rule (1×) share this predicate — the stage difference is data, not
 * code (§7.2). Combination with employment income is FIN.COMBINE's job.
 */
import type { EvaluatePredicate } from "./predicate.js";
import { savingsSufficiency } from "./savings_formula.js";

export const cash_savings_sufficiency: EvaluatePredicate = (facts, rule, ctx) => {
  const thresholdResult = ctx.results.get("FIN.THRESHOLD");
  if (!thresholdResult || thresholdResult.status !== "MET") {
    return {
      status: "PENDING_EVIDENCE",
      because: ["FIN.THRESHOLD"],
      notes: ["The savings requirement cannot be calculated until the applicable income threshold is determined."],
    };
  }
  const threshold = thresholdResult.figures?.["threshold"];
  if (threshold === undefined) {
    throw new Error("FIN.THRESHOLD result carries no threshold figure — ruleset/engine mismatch");
  }

  return savingsSufficiency({
    facts,
    threshold,
    income: 0,
    floor: numeric(ctx.paramFigure(rule, "floor_figure")),
    multiplier: numeric(ctx.paramFigure(rule, "multiplier_figure")),
    holdingMonths: numeric(ctx.paramFigure(rule, "holding_months_figure")),
  });
};

function numeric(value: number | string): number {
  if (typeof value !== "number") throw new Error(`Expected a numeric figure, got: ${value}`);
  return value;
}
